import { useState } from "react";
import { HorseArt } from "@/components/game/HorseArt";
import { StatBar } from "@/components/game/StatBar";
import { DailyCare } from "@/components/game/DailyCare";
import { ReadinessMeter } from "@/components/game/ReadinessMeter";
import { CORE_GAME_FOR } from "@/components/game/CoreMinigames";
import { MinigameGate } from "@/components/game/MinigameGate";
import { Zap, Heart, Sparkles, Coffee, Lock, Trophy, ChevronDown, User, ScrollText, FastForward } from "lucide-react";

const FILTERS = ["Available Now", "Selected", "Locked", "All Activities"];
const STAT_FILTERS = ["All", "Trust", "Handling", "Manners", "Confidence", "Conditioning", "Trail", "Freestyle", "Riding", "Liberty", "Popularity"];

const moodLabel = (m) => (m >= 80 ? "Happy" : m >= 55 ? "Content" : m >= 35 ? "Unsettled" : "Grumpy");
const STAT_KEYS = ["handling", "manners", "confidence", "conditioning", "trail", "freestyle", "riding", "liberty", "popularity"];
const STAT_LABEL = { handling: "Handling", manners: "Manners", confidence: "Confidence", conditioning: "Conditioning", trail: "Trail", freestyle: "Freestyle", riding: "Riding", liberty: "Liberty", popularity: "Popularity" };

export const TrainingDay = ({ save, catalog, onTrain, onEndDay, onCare, onOpenShop, onOpenJournal, onOpenProfile, onSkip, shop, inventory, busy, stableActions, hideCare, onGoCare }) => {
  const { horse, stats, day } = save;
  const learned = save.learned_skills || [];
  const [selected, setSelected] = useState([]);
  const [filter, setFilter] = useState("Available Now");
  const [statFilter, setStatFilter] = useState("All");
  const [expanded, setExpanded] = useState({});
  const [coreGame, setCoreGame] = useState(null);
  const careDone = (save.care?.hay && save.care?.water) || save.groom_active;
  const trained = save.care?.trained;
  const isStable = save.status === "stable";

  const flat = catalogFlat(catalog);
  const energyUsed = selected.reduce((sum, id) => sum + (flat[id]?.energy || 0), 0);

  const isLocked = (a) => {
    const missing = (a.prereq || []).filter((p) => !learned.includes(p));
    if (missing.length) return `Needs: ${missing.join(", ")}`;
    if (a.trust_gate && stats.trust < a.trust_gate) return `Needs trust ${a.trust_gate}+`;
    if (a.energy > stats.energy) return `Needs ${a.energy} energy`;
    if (!selected.includes(a.id) && a.energy > stats.energy - energyUsed) return "Not enough energy left";
    return null;
  };

  const toggle = (a) => {
    if (isLocked(a)) return;
    if (selected.includes(a.id)) { setSelected(selected.filter((x) => x !== a.id)); return; }
    if (a.hard || CORE_GAME_FOR[a.id]) { setSelected([a.id]); return; }
    if (selected.some((id) => flat[id]?.hard || CORE_GAME_FOR[id])) { setSelected([a.id]); return; }
    if (selected.length >= 2) return;
    setSelected([...selected, a.id]);
  };

  const coreSelected = selected.length === 1 && CORE_GAME_FOR[selected[0]];

  const doTrain = async (rest) => {
    if (!rest && coreSelected) { setCoreGame(selected[0]); return; }
    await onTrain(rest ? { rest: true } : { activities: selected });
    setSelected([]);
  };

  const cats = Object.entries(catalog || {}).map(([cat, acts]) => [
    cat, acts.filter((a) => !a.division || a.division === horse.division),
  ]).filter(([, acts]) => acts.length);

  return (
    <div className="px-4 pb-8" data-testid="training-day">
      {/* progress */}
      {!isStable && (
        <>
          <div className="mt-2 flex items-center justify-between text-sm font-bold text-primary">
            <span data-testid="day-counter">Day {day} of 120</span>
            <span className="text-secondary">{Math.round((day / 120) * 100)}%</span>
          </div>
          <div className="mt-1 h-2 overflow-hidden rounded-full bg-muted">
            <div className="h-full rounded-full bg-secondary transition-all duration-500" style={{ width: `${(day / 120) * 100}%` }} />
          </div>
          {day <= 110 && !trained && save.status === "training" && (
            <button data-testid="skip-ahead-btn" onClick={onSkip}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 py-2.5 text-sm font-bold text-primary active:scale-95">
              <FastForward size={15} /> Skip Ahead 10 Days
            </button>
          )}
        </>
      )}

      {stableActions}

      {/* horse header */}
      <div className="mt-4 flex gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm">
        <div className="grid h-24 w-24 shrink-0 place-items-center rounded-xl" style={{ background: `${horse.color_hex}1a` }}>
          <HorseArt src={horse.art_image} color={horse.color_hex} size={88} />
        </div>
        <div className="flex-1">
          <h2 className="font-display text-xl font-bold text-primary">{horse.name}</h2>
          <p className="text-xs font-semibold text-secondary">{horse.hma} · {horse.division}</p>
          <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
            <span className="flex items-center gap-1"><Heart size={12} className="text-red-500" /> Trust {Math.round(stats.trust || 0)}</span>
            <span className="flex items-center gap-1"><Zap size={12} className="text-amber-500" /> Energy {Math.round(stats.energy)}</span>
            <span className="flex items-center gap-1"><Sparkles size={12} className="text-green-600" /> Health {Math.round(stats.health)}</span>
            <span className="flex items-center gap-1"><Coffee size={12} className="text-primary" /> {moodLabel(stats.mood)}</span>
          </div>
        </div>
      </div>

      {/* discovered traits */}
      {horse.discovered_traits?.length > 0 && (
        <div className="mt-3 rounded-xl border border-secondary/40 bg-secondary/5 p-3">
          <p className="text-xs font-bold uppercase tracking-wider text-secondary">Discovered Traits</p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {horse.discovered_traits.map((t) => (
              <span key={t} className="rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-primary shadow-sm">{t}</span>
            ))}
          </div>
        </div>
      )}

      {/* stats */}
      <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 rounded-2xl border border-border bg-card p-4 shadow-sm">
        {STAT_KEYS.map((k) => <StatBar key={k} label={STAT_LABEL[k]} value={stats[k] || 0} tone={k === "popularity" ? "secondary" : "primary"} />)}
      </div>

      <ReadinessMeter readiness={save.readiness} coreSkills={save.readiness?.core_all} />

      {save.status === "ready_to_compete" ? (
        <div className="mt-4 rounded-2xl border border-secondary/50 bg-secondary/10 p-5 text-center" data-testid="ready-to-compete">
          <Trophy size={30} className="mx-auto text-secondary" />
          <p className="mt-2 font-display text-xl font-bold text-primary">{horse.name} is ready to compete!</p>
          <p className="mt-1 text-sm text-muted-foreground">The Makeover competition, judging, keep-or-sell and the auction arrive in the next update. Great job on 120 days!</p>
        </div>
      ) : (
        <>
          {hideCare
            ? (!careDone && <button type="button" data-testid="care-reminder" onClick={onGoCare}
                className="mt-2 w-full rounded-xl border border-amber-300 bg-amber-50 p-3 text-left text-xs font-semibold text-amber-800">🥕 Daily care comes first — tap to open the <span className="underline">Care</span> tab and feed &amp; water. Training unlocks once care is done.</button>)
            : <DailyCare save={save} shop={shop} inventory={inventory} onCare={onCare} onOpenShop={onOpenShop} busy={busy} />}

          {!careDone ? null : (
          <>
          {(() => {
            const anyAvail = cats.some(([, a]) => a.some((x) => !isLocked(x)));
            if (!anyAvail) return (
              <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs font-semibold text-amber-800" data-testid="no-energy-notice">
                {horse.name} is out of energy ({Math.round(stats.energy)}⚡). Every activity costs energy, so none are available right now — take a <span className="font-bold">Rest &amp; Care Day</span> below to recharge to full.
              </div>
            );
            if (stats.energy < 30) return (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/70 p-2.5 text-[11px] font-semibold text-amber-800" data-testid="low-energy-notice">
                Low energy ({Math.round(stats.energy)}⚡): activities that cost more energy than {horse.name} has left are hidden under “Available Now.” Switch to the <span className="font-bold">Locked</span> filter to see them, or take a Rest &amp; Care Day.
              </div>
            );
            return null;
          })()}
          {/* filters */}
          <div className="mt-4 flex gap-2 overflow-x-auto pb-1" data-testid="activity-filters">
            {FILTERS.map((f) => (
              <button key={f} data-testid={`filter-${f.replace(/\s/g, "-")}`} onClick={() => setFilter(f)}
                className={`whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-bold ${filter === f ? "border-secondary bg-secondary text-white" : "border-border bg-card text-muted-foreground"}`}>{f}</button>
            ))}
          </div>

          {/* target-stat filters */}
          <div className="mt-2 flex gap-2 overflow-x-auto pb-1" data-testid="stat-filters">
            {STAT_FILTERS.map((s) => (
              <button key={s} data-testid={`statfilter-${s}`} onClick={() => setStatFilter(s)}
                className={`whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-bold ${statFilter === s ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-muted-foreground"}`}>{s}</button>
            ))}
          </div>

          <div className="mt-2 space-y-2">
            {(() => {
              const autoOpen = (cats.find(([, a]) => a.some((x) => !isLocked(x))) || [])[0];
              return cats.map(([cat, acts]) => {
                const avail = acts.filter((a) => !isLocked(a));
                const shown = acts.filter((a) => (filter === "All Activities" ? true : filter === "Available Now" ? !isLocked(a) : filter === "Selected" ? selected.includes(a.id) : isLocked(a)) && (statFilter === "All" || (a.eff && a.eff[statFilter.toLowerCase()])));
                const open = expanded[cat] !== undefined ? expanded[cat] : cat === autoOpen;
                return (
                  <div key={cat} className="overflow-hidden rounded-xl border border-border bg-card">
                    <button data-testid={`cat-${cat.replace(/\s/g, "-")}`} onClick={() => setExpanded({ ...expanded, [cat]: !open })}
                      className="flex w-full items-center justify-between px-3 py-2.5 text-left">
                      <span className="text-sm font-bold text-primary">{cat}{selected.some((id) => acts.find((a) => a.id === id)) ? " •" : ""}</span>
                      <span className="flex items-center gap-2 text-[11px] text-muted-foreground">
                        <span className="font-semibold text-green-600">{avail.length} avail</span>·<span>{acts.length - avail.length} locked</span>
                        <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
                      </span>
                    </button>
                    {open && (
                      <div className="grid grid-cols-2 gap-2 px-3 pb-3">
                        {shown.length === 0 && <p className="col-span-2 text-xs text-muted-foreground">Nothing here for this filter.</p>}
                        {shown.map((a) => {
                          const lock = isLocked(a);
                          const on = selected.includes(a.id);
                          const done = a.skill && learned.includes(a.skill);
                          return (
                            <button key={a.id} data-testid={`activity-${a.id}`} disabled={!!lock || busy} onClick={() => toggle(a)}
                              className={`relative rounded-xl border p-2 text-left text-xs transition-all ${on ? "border-secondary bg-secondary/10 ring-1 ring-secondary" : lock ? "border-border bg-muted/40 opacity-70" : "border-border bg-card active:scale-95"}`}>
                              <span className="block font-bold text-primary">{a.name}{a.required ? <span className="ml-1 rounded bg-primary/10 px-1 py-0.5 text-[8px] font-bold uppercase text-primary">Core</span> : null}</span>
                              <span className="flex items-center gap-1 text-[10px] text-muted-foreground"><Zap size={9} /> {a.energy}{a.hard ? " · full day" : ""}{done ? " · ✓" : ""}</span>
                              {a.eff && Object.keys(a.eff).length > 0 && (
                                <span className="mt-1 flex flex-wrap gap-1">
                                  {Object.keys(a.eff).map((k) => (
                                    <span key={k} className="rounded bg-secondary/15 px-1.5 py-0.5 text-[9px] font-bold capitalize text-secondary">{k} +{a.eff[k]}</span>
                                  ))}
                                </span>
                              )}
                              {a.facility_bonus && Object.keys(a.facility_bonus).length > 0 && (
                                <span className="mt-1 flex flex-wrap items-center gap-1" data-testid={`facility-bonus-${a.id}`}>
                                  {Object.keys(a.facility_bonus).map((k) => (
                                    <span key={k} className="rounded bg-green-100 px-1.5 py-0.5 text-[9px] font-bold capitalize text-green-700">+{a.facility_bonus[k]} {k}</span>
                                  ))}
                                  <span className="text-[8px] font-semibold text-green-700">{(a.facility_sources || []).join(", ")}</span>
                                </span>
                              )}
                              {lock && <span className="mt-0.5 flex items-center gap-1 text-[10px] font-semibold text-amber-700"><Lock size={9} /> {lock}</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>

          {selected.length > 0 && (
            <p className="mt-2 text-xs font-semibold text-secondary" data-testid="selected-summary">Selected: {selected.map((id) => flat[id]?.name).join(", ")}{coreSelected ? " · a mini-game lesson 🎮" : ""}</p>
          )}

          <div className="mt-3 flex gap-2">
            {trained ? (
              <button data-testid="end-day-btn" disabled={busy} onClick={onEndDay}
                className="h-12 w-full rounded-xl bg-secondary font-bold text-white disabled:opacity-60 active:scale-95">
                {busy ? "Ending day…" : isStable ? "End Session →" : `End Day ${day} →`}
              </button>
            ) : (
              <>
                <button data-testid="rest-day-btn" disabled={busy} onClick={() => doTrain(true)}
                  className="h-12 flex-1 rounded-xl border-2 border-primary bg-card font-bold text-primary disabled:opacity-60 active:scale-95">
                  Rest & Care Day · +100⚡
                </button>
                <button data-testid="train-day-btn" disabled={busy || selected.length === 0} onClick={() => doTrain(false)}
                  className="h-12 flex-[2] rounded-xl bg-primary font-bold text-primary-foreground disabled:opacity-40 active:scale-95">
                  {busy ? "Training…" : coreSelected ? "Start Core Lesson 🎮" : isStable ? "Train" : `Train Day ${day}`}
                </button>
              </>
            )}
          </div>
          {trained && <p className="mt-2 text-center text-xs font-semibold text-secondary" data-testid="trained-msg">Today's session is done. Add any extra care, then End Day when ready.</p>}
          </>
          )}
        </>
      )}

      {/* journal preview */}
      <div className="mt-5 flex items-center justify-between">
        <p className="text-sm font-bold text-primary">Journal</p>
        <button data-testid="view-journal-btn" onClick={onOpenJournal} className="text-xs font-semibold text-secondary">View Full Journal →</button>
      </div>
      <div className="mt-1 space-y-1.5" data-testid="game-journal">
        {[...(save.log || [])].reverse().slice(0, 3).map((e, i) => (
          <div key={i} className="rounded-lg border border-border bg-card px-3 py-1.5 text-xs text-foreground/80">
            <span className="font-bold text-secondary">Day {e.day}:</span> {e.text}
          </div>
        ))}
      </div>

      {coreGame && (
        <div className="fixed inset-0 z-[84] flex items-center justify-center bg-black/60 p-4" data-testid="core-game-modal">
          <div className="w-full max-w-sm">
            {(() => { const G = CORE_GAME_FOR[coreGame]; return <MinigameGate gameKey={coreGame}>{() => <G onDone={(perf) => { setCoreGame(null); onTrain({ activities: [coreGame], perf }); setSelected([]); }} />}</MinigameGate>; })()}
            <button data-testid="core-game-cancel" onClick={() => setCoreGame(null)}
              className="mt-2 h-10 w-full rounded-xl border border-white/40 bg-white/10 text-sm font-bold text-white">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

function catalogFlat(catalog) {
  const flat = {};
  Object.values(catalog || {}).forEach((acts) => acts.forEach((a) => { flat[a.id] = a; }));
  return flat;
}
